// withFetchProductData.js
'use strict';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { LRUCache } from 'lru-cache';

import { MESSAGE_PACK_FEED_ALIAS } from '../RemoteConfigKeys.js';

import getListData from '../selector/getListData.js';
import getFeedName from '../selector/getFeedName.js';
import getProductData from '../selector/getProductData.js';
import getOperationData from '../selector/getOperationData.js';

import fetchFeeds from '../action/fetchFeeds.js';
import fetchProductDetail from '../action/fetchProductDetail.js';

import { getFeedNameType } from '../resource/feedUtils.js';
import { ProductType } from '../resource/shopConstants.js';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NULL_FUNCTION,
} from '../resource/defaults.js';

const cache = new LRUCache({ max: 256 });
const getSelectPath = ({ feedName } = {}) => {
  const key = `${feedName}`;
  let result = cache.get(key);
  if (!result) {
    result = ['home', getFeedNameType({ feedName }), feedName];
    cache.set(key, result);
  }
  return result;
};

const withFetchProductData = BaseComponent => {
  function FetchProductData(props) {
    const {
      shopId = null,
      feedName = null,
      productId = null,
      messageIds = EMPTY_ARRAY,
      totalCount = null,
      staticContext = EMPTY_OBJECT,
      children = NULL_FUNCTION,
      fetchFeeds = NULL_FUNCTION,
      fetchProductDetail = NULL_FUNCTION,
    } = props;
    const propsRef = useRef({});
    const prevFeedName = useRef(feedName);
    const prevProductId = useRef(productId);
    propsRef.current = {
      feedName,
      productId,
      staticContext,
      fetchFeeds,
      fetchProductDetail,
    };

    const fetchFeedsData = () => {
      const { feedName, staticContext, fetchFeeds } = propsRef.current;
      if (!feedName) {
        return;
      }
      return fetchFeeds({
        type: feedName,
        httpProxyHeaders: staticContext.httpProxyHeaders,
      });
    };

    const fetchProductDetailData = () => {
      const { productId, staticContext, fetchProductDetail } = propsRef.current;
      if (!productId) {
        return;
      }
      return fetchProductDetail({
        productId,
        httpProxyHeaders: staticContext.httpProxyHeaders,
      });
    };

    useEffect(() => {
      fetchFeedsData();
      fetchProductDetailData();
    }, []);

    useEffect(() => {
      const isFeedNameChanged = feedName !== prevFeedName.current;
      const isProductIdChanged = productId !== prevProductId.current;
      if (isFeedNameChanged) {
        fetchFeedsData();
      }
      if (isProductIdChanged) {
        fetchProductDetailData();
      }
      prevFeedName.current = feedName;
      prevProductId.current = productId;
    }, [feedName, productId]);

    return (
      <BaseComponent
        {...props}
        shopId={shopId}
        feedName={feedName}
        productId={productId}
        messageIds={messageIds}
        totalCount={totalCount}
        staticContext={staticContext}
        fetchFeeds={fetchFeeds}
        fetchProductDetail={fetchProductDetail}
      >
        {children}
      </BaseComponent>
    );
  }

  FetchProductData.propTypes = {
    shopId: PropTypes.string,
    feedName: PropTypes.string,
    productId: PropTypes.string,
    messageIds: PropTypes.array,
    totalCount: PropTypes.number,
    staticContext: PropTypes.object,
    children: PropTypes.func,
    fetchFeeds: PropTypes.func,
    fetchProductDetail: PropTypes.func,
  };

  const mapStateToProps = (state, { productId }) => {
    const shopId = getOperationData(state, ['shop'], 'usingShopId');
    const productType = getProductData(state, [productId], 'type');
    const feedName =
      ProductType.MESSAGEPACKS === productType
        ? getFeedName(state, MESSAGE_PACK_FEED_ALIAS, '{PRODUCT_ID}', productId)
        : undefined;
    const listSelectPath = getSelectPath({ feedName });
    return {
      shopId,
      feedName,
      productId,
      messageIds: getListData(state, listSelectPath, 'itemIds'),
      totalCount: getListData(state, listSelectPath, 'totalCount'),
    };
  };

  const mapDispatchToProps = dispatch => {
    return {
      fetchFeeds: data => dispatch(fetchFeeds(data)),
      fetchProductDetail: data => dispatch(fetchProductDetail(data)),
    };
  };

  return connect(mapStateToProps, mapDispatchToProps)(FetchProductData);
};

export default withFetchProductData;
